import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { paymentAPI } from '../../../services/api';

// Mock components - replace with your actual components
const Spacing = ({ lg, md }) => <div style={{ height: `${lg}px` }} />;
const SectionHeading = ({ title, subTitle, variant }) => (
  <div className={variant}>
    <h1>{title}</h1>
    <p>{subTitle}</p>
  </div>
);

export default function Checkout() {
  const [user, setUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [paymentStep, setPaymentStep] = useState('initial');
  const [orderData, setOrderData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(900);
  const [upiRefId, setUpiRefId] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const navigate = useNavigate();

  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout-ui.shiprocket.com/assets/js/channels/shopify.js';
    script.async = true;
    document.body.appendChild(script);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://checkout-ui.shiprocket.com/assets/styles/shopify.css';
    document.head.appendChild(link);

    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.id = 'sellerDomain';
    hiddenInput.value = window.location.hostname;
    document.body.appendChild(hiddenInput);

    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
      if (document.head.contains(link)) document.head.removeChild(link);
      if (document.body.contains(hiddenInput)) document.body.removeChild(hiddenInput);
    };
  }, []);

  const sanitizeProductData = (data) => {
    if (!data) return data;
    const sensitiveTerms = [
      /male enhancement/gi,
      /performance enhancement/gi,
      /sexual wellness/gi,
      /sex drive/gi,
      /erection/gi,
      /libido/gi,
      /last longer/gi,
      /timing/gi,
      /intercourse/gi
    ];
    
    let sanitized = { ...data };
    const replacement = 'wellness';

    if (sanitized.product_name) {
      sensitiveTerms.forEach(term => {
        sanitized.product_name = sanitized.product_name.replace(term, 'Wellness');
      });
    }

    if (sanitized.short_description) {
      sensitiveTerms.forEach(term => {
        sanitized.short_description = sanitized.short_description.replace(term, replacement);
      });
    }

    if (sanitized.description) {
      sensitiveTerms.forEach(term => {
        sanitized.description = sanitized.description.replace(term, replacement);
      });
    }

    if (sanitized.other_description?.specifications) {
      const specs = sanitized.other_description.specifications;
      Object.keys(specs).forEach(key => {
        sensitiveTerms.forEach(term => {
          specs[key] = specs[key].replace(term, replacement);
        });
      });
    }

    return sanitized;
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentStep === 'qr_display' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            toast.error('Payment session expired. Please try again.');
            setPaymentStep('initial');
            return 900;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [paymentStep, timeLeft]);

  const fetchProduct = () => {
    const productData = localStorage.getItem('checkout_product');
    if (productData) {
      setProduct(sanitizeProductData(JSON.parse(productData)));
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCreateOrder = async () => {
    if (!product) {
      toast.error('Product information not available');
      return;
    }

    if (!user) {
      if (!guestName.trim()) {
        toast.error('Please enter your name');
        return;
      }
      if (!guestEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (!guestPhone.trim() || !/^\d{10}$/.test(guestPhone)) {
        toast.error('Please enter a valid 10-digit phone number');
        return;
      }
    }

    setLoading(true);
    try {
      const orderPayload = {
        product_id: product.id || product.product_id || 1,
        qty: quantity,
        guest_name: user ? (user.name || '') : guestName,
        guest_email: user ? (user.email || '') : guestEmail,
        guest_phone: user ? (user.mobile || '') : guestPhone
      };
      console.log(orderPayload)

      const result = await paymentAPI.initiateCheckout(orderPayload);

      console.log('Checkout API Response:', result);
      
      if (result.data.status === 200) {
        const token = result.data.data?.access_token || result.data.data?.token;
        
        if (token) {
          
          if (window.HeadlessCheckout && window.HeadlessCheckout.addToCart) {
            window.HeadlessCheckout.addToCart(
              { preventDefault: () => {} },
              token,
              { fallbackUrl: `${window.location.origin}/payment-success` }
            );
          } else {
            console.error('Shiprocket checkout script not loaded');
            toast.error('Checkout system not ready. Please refresh and try again.');
          }
        } else {
          console.error('No token in response:', result.data.data);
          toast.error('Unable to initialize checkout. Please contact support.');
        }
      } else {
        toast.error(result.data.message || 'Failed to initiate checkout');
      }
    } catch (error) {
      console.error('Checkout initiation error:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to initiate checkout. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUPI = () => {
    if (orderData?.upi_intent) {
      navigator.clipboard.writeText(orderData.upi_intent);
      toast.success('UPI link copied to clipboard!');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setScreenshot(file);
    }
  };

  const handleSubmitProof = async () => {
    if (!upiRefId.trim()) {
      toast.error('Please enter UPI Reference ID');
      return;
    }
    if (!screenshot) {
      toast.error('Please upload screen shot of payment');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('order_id', orderData.order_id);
      formData.append('transaction_id', orderData.transaction_id);
      formData.append('upi_ref_id', upiRefId);
      if (screenshot) {
        formData.append('proof_image', screenshot);
      }

      // Replace with your actual API call
      const result = await paymentAPI.uploadProof(formData);
      console.log(result)
      if (result.data.status === 200) {
        toast.success('Payment proof submitted! Awaiting admin verification.');
        navigate('/profile');
      } else {
        toast.error(result.data.message || 'Failed to submit payment proof');
      }
    } catch (error) {
      console.error('Submit proof error:', error);
      toast.error('Failed to submit payment proof. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = (upiIntent) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(upiIntent)}`;
  };

  console.log(generateQRCode)

  return (
    <>
    <ToastContainer/>
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <SectionHeading
        title="Checkout"
        subTitle="Complete Your Order"
        variant="text-center"
      />
      <Spacing lg="75" md="60" />
      <div className="container">
        <span 
          className="cs_fs_16 cs_accent_color mb-4 d-inline-block"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          ← Go Back
        </span>
        <div className="row">
          {/* Billing Details */}
          <div className="col-xl-7">
            <h2 className="cs_checkout-title">Billing Details</h2>
            <Spacing lg="40" md="40" />
            <div className="row">
              {user ? (
                <>
                  <div className="col-lg-12">
                    <label className="cs_shop-label">Full Name *</label>
                    <input 
                      type="text" 
                      className="cs_shop-input" 
                      value={user.name || ''}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-12">
                    <label className="cs_shop-label">Mobile Number *</label>
                    <input 
                      type="text" 
                      className="cs_shop-input" 
                      value={user.mobile || ''}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-12">
                    <label className="cs_shop-label">Email Address *</label>
                    <input 
                      type="email" 
                      className="cs_shop-input" 
                      value={user.email || ''}
                      readOnly
                    />
                  </div>
                  {user.address && (
                    <div className="col-lg-12">
                      <label className="cs_shop-label">Address *</label>
                      <textarea 
                        className="cs_shop-input" 
                        rows={3}
                        value={user.address || ''}
                        readOnly
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="col-lg-12">
                    <label className="cs_shop-label">Full Name *</label>
                    <input 
                      type="text" 
                      className="cs_shop-input" 
                      placeholder="Enter your full name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                    />
                  </div>
                  <div className="col-lg-12">
                    <label className="cs_shop-label">Mobile Number *</label>
                    <input 
                      type="tel" 
                      className="cs_shop-input" 
                      placeholder="Enter 10-digit mobile number"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      maxLength={10}
                    />
                  </div>
                  <div className="col-lg-12">
                    <label className="cs_shop-label">Email Address *</label>
                    <input 
                      type="email" 
                      className="cs_shop-input" 
                      placeholder="Enter your email address"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
            <Spacing lg="20" md="20" />
            <Spacing lg="30" md="30" />
          </div>

          {/* Order Summary & Payment */}
          <div className="col-xl-5">
            <div className="cs_shop-side-spacing">
              <div className="cs_shop-card">
                <h2>Your order</h2>
                <table>
                  <tbody>
                    <tr className="cs_semi_bold">
                      <td>Product</td>
                      <td className="text-end">Amount</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{product ? product.product_name : 'Ayumedix Wellness Capsule'}</span>
                          <div className="cs_qty_controls d-flex align-items-center">
                            <button 
                              type="button"
                              className="cs_qty_btn"
                              onClick={() => setQuantity(Math.max(1, quantity - 1))}
                              disabled={paymentStep !== 'initial'}
                              style={{background: '#0b5e4b', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px'}}
                            >
                              -
                            </button>
                            <span className="cs_qty_display" style={{margin: '0 10px', fontWeight: 'bold'}}>{quantity}</span>
                            <button 
                              type="button"
                              className="cs_qty_btn"
                              onClick={() => setQuantity(quantity + 1)}
                              disabled={paymentStep !== 'initial'}
                              style={{background: '#0b5e4b', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px'}}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="text-end">₹{product ? (product.price * quantity) : (2999 * quantity)}</td>
                    </tr>
                    <tr className="cs_semi_bold">
                      <td>Total</td>
                      <td className="text-end">₹{product ? (product.price * quantity) : (2999 * quantity)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <Spacing lg="50" md="30" />

              {/* Payment Section */}
              <div className="cs_shop-card">
                <h2>Payment</h2>
                
                {paymentStep === 'initial' && (
                  <>
                    <div className="cs_payment_method">
                      <div className="cs_razorpay_info">
                        <h4>Secure Checkout</h4>
                        <p className="cs_payment_text">
                          Complete your payment securely through Shiprocket Checkout. We support UPI, Cards, Net Banking, and Cash on Delivery.
                        </p>
                      </div>
                    </div>
                    <Spacing lg="25" md="25" />
                    <p className="m-0 cs_payment_text">
                      Your personal data will be used to process your order and for other purposes described in our privacy policy. All payments are processed securely.
                    </p>
                    <Spacing lg="20" md="20" />
                    <button 
                      className="cs_product_btn cs_semi_bold w-100"
                      onClick={handleCreateOrder}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : `Proceed to Pay - ₹${product ? (product.price * quantity) : (2999 * quantity)}`}
                    </button>
                  </>
                )}

                {paymentStep === 'qr_display' && orderData && (
                  <div className="text-center">
                    <div className="alert alert-warning mb-3">
                      <strong>⏱️ Time Remaining: {formatTime(timeLeft)}</strong>
                    </div>
                    
                    <h4 className="mb-3">Scan QR Code to Pay</h4>
                    <img 
                      src={generateQRCode(orderData.upi_intent)} 
                      alt="UPI QR Code"
                      style={{ maxWidth: '300px', width: '100%', border: '2px solid #ddd', padding: '10px', borderRadius: '10px' }}
                    />
                    
                    <Spacing lg="20" md="20" />
                    
                    <div className="text-start bg-light p-3 rounded mb-3">
                      <small className="d-block mb-2"><strong>Order ID:</strong> {orderData.order_id}</small>
                      <small className="d-block mb-2"><strong>Amount:</strong> ₹{orderData.amount}</small>
                      <small className="d-block"><strong>UPI Link:</strong></small>
                      <div className="d-flex gap-2 mt-2">
                        <input 
                          type="text" 
                          value={orderData.upi_intent} 
                          readOnly 
                          className="form-control form-control-sm"
                          style={{ fontSize: '12px' }}
                        />
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleCopyUPI}
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <button 
                      className="cs_product_btn cs_semi_bold w-100 mb-2"
                      onClick={() => setPaymentStep('submit_proof')}
                    >
                      I've Paid - Submit Proof
                    </button>
                    
                    <button 
                      className="btn btn-link w-100"
                      onClick={() => {
                        setPaymentStep('initial');
                        setOrderData(null);
                        setTimeLeft(900);
                      }}
                    >
                      Cancel Payment
                    </button>
                  </div>
                )}

                {paymentStep === 'submit_proof' && orderData && (
                  <div>
                    <h4 className="mb-3">Submit Payment Proof</h4>
                    <p className="text-muted small mb-3">
                      Please provide your UPI Reference ID (12-digit number) to verify payment.
                    </p>
                    
                    <div className="mb-3">
                      <label className="cs_shop-label">UPI Reference ID *</label>
                      <input 
                        type="text" 
                        className="cs_shop-input" 
                        placeholder="e.g., 123456789012"
                        value={upiRefId}
                        onChange={(e) => setUpiRefId(e.target.value)}
                        maxLength={12}
                      />
                      <small className="text-muted">Find this in your UPI app payment history</small>
                    </div>

                    <div className="mb-3">
                      <label className="cs_shop-label">Payment Screenshot *</label>
                      <input 
                        type="file" 
                        className="cs_shop-input" 
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      <small className="text-muted">Upload screenshot from your UPI app (Max 2MB)</small>
                    </div>

                    <div className="alert alert-info mb-3">
                      <small>
                        <strong>Order ID:</strong> {orderData.order_id}<br />
                        <strong>Amount Paid:</strong> ₹{orderData.amount}<br />
                        Your order will be verified within 24 hours.
                      </small>
                    </div>

                    <button 
                      className="cs_product_btn cs_semi_bold w-100 mb-2"
                      onClick={handleSubmitProof}
                      disabled={loading || !upiRefId.trim()}
                    >
                      {loading ? 'Submitting...' : 'Submit Payment Proof'}
                    </button>

                    <button 
                      className="btn btn-link w-100"
                      onClick={() => setPaymentStep('qr_display')}
                    >
                      ← Back to QR Code
                    </button>
                  </div>
                )}
              </div>
              <Spacing lg="30" md="30" />
            </div>
          </div>
        </div>
      </div>
      <Spacing lg="120" md="50" />
      
      {/* Edit Details Modal */}
      {showConfirmModal && (
        <div className="cs_modal_overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="cs_modal cs_white_bg cs_radius_15" style={{
            padding: '40px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 className="cs_fs_24 cs_semibold mb-3">Edit Details</h3>
            <p className="cs_fs_16 mb-4">
              Are you sure you want to edit your details? You will be redirected to the profile page.
            </p>
            <div className="d-flex gap-3 justify-content-center">
              <button 
                className="cs_btn cs_style_1 cs_fs_14 cs_rounded_5 cs_pl_20 cs_pr_20 cs_pt_10 cs_pb_10"
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid #0b5e4b',
                  color: '#0b5e4b'
                }}
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button 
                className="cs_btn cs_style_1 cs_fs_14 cs_rounded_5 cs_pl_20 cs_pr_20 cs_pt_10 cs_pb_10 cs_accent_bg"
                onClick={() => {
                  setShowConfirmModal(false);
                  navigate('/profile');
                }}
              >
                Yes, Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}